import { Box, Button, Divider, IconButton, Stack } from '@mui/material';
import { CreateSellerRequestTitle } from './ui/create-seller-request-title';
import { CreateSellerRequestStatus } from './ui/create-seller-request-status';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UIFormLabel } from '@/shared/ui/ui-form-label';

import { CreateDealChoseDocument } from './ui/create-seller-request-document';
import { CreateSellerRequestActions } from './ui/create-seller-request-actions';
import {
  useGetFactorBuyersQuery,
  useGetSellerRequestByIdQuery,
  useLazyGetFactorBuyersQuery,
  useLazyGetFactorsQuery,
} from '@/app/api';
import {
  CreateSellerRequestValues,
  RequestDocumentDidoxType,
  RequestDocumentFileType,
} from './types';
import { DidoxDocumentsModal } from '@/shared/modals/didox-documents-modal';
import { FC, useEffect, useRef, useState } from 'react';
import { DidoxDocumentsModalRef } from '@/shared/modals/didox-documents-modal/types';
import PaperClipIcon from '@/app/assets/icons/paperclip.svg?react';
import XmarkIcon from '@/app/assets/icons/xmark.svg?react';
import { useParams, useSearchParams } from 'react-router-dom';
import { UILoader } from '@/shared/ui/ui-loader';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { getFetchEror } from '@/app/helpers';
import { useLazyGetDocsQuery } from '@/entities/didox/api';
import dayjs from 'dayjs';
import { DidoxDocument, GetDidoxDocuments } from '@/entities/didox/types';
import { Buyer } from '@/app/api/types';
import { useAppSelector } from '@/app/hooks';
import _ from 'lodash';
import FullscreenLoader from '@/shared/FullscreenLoader/FullscreenLoader';
import { BaseModalRef } from '@/app/helpers/types';
import useCreateSellerRequestFormSideEffects from './useCreateSellerRequestFormSideEffects';
import { RenderDealRows } from './ui/render-deal-rows';

interface Props {
  type: 'seller' | 'buyer';
}

export const CreateSellerRequest: FC<Props> = ({ type }) => {
  const { t } = useTranslation('createDealForm');

  const debtorOptions = [
    {
      value: 0,
      name: t('common:buyer'),
    },
    {
      value: 1,
      name: t('common:supplier'),
    },
  ];

  const { showNotification } = useNotification();
  const { id = '' } = useParams<{ id: string }>();
  const { profile } = useAppSelector((state) => state.auth);
  const [sp] = useSearchParams();

  const defaultDocuments = [
    { value: null, type: '007', required: true },
    { value: null, type: '002', required: true, additionTypes: ['008'] },
    { value: null, type: '052', required: false },
    { value: null, type: '075', required: false },
  ];
  const form = useForm<CreateSellerRequestValues>({
    defaultValues: {
      buyer: null,
      factor: null,
      contractAmount: '',
      buyerDebtAmount: '',
      contractPaymentDueDate: dayjs(),
      documents: defaultDocuments,
      branchId: null,
      currency:
        sp.get('isInternationalFactoring') === 'true' ? null : 'UZS',
    },
  });
  const { control, watch, reset, getValues } = form;

  const [additionalDocs, setAdditionalDocs] = useState<
    { value: File | string; unique?: number; name?: string }[]
  >([]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  const buyer = watch('buyer');
  const sellerTin = watch('sellerTin');
  const documents = watch('documents');
  const isContractAdded = documents?.some(
    (doc) => doc.type === '007' && doc.value
  );
  const isInvoiceAdded = documents?.some(
    (doc) => (doc.type === '002' || doc.type === '008') && doc.value
  );

  const invoiceDocs = (documents ?? [])
    .filter(
      (doc) =>
        (doc.type === '002' || doc.type === '008') &&
        doc.value !== null &&
        typeof doc.value === 'object' &&
        'doc_id' in doc.value
    )
    .map((doc) => doc.value as RequestDocumentDidoxType);

  const invoiceDocIds = invoiceDocs.map((doc) => doc.doc_id).join(',');

  useEffect(() => {
    if (invoiceDocs.length) {
      const total = invoiceDocs.reduce((sum, doc) => {
        const isPartial =
          Array.isArray(doc.factoringBlocks) &&
          doc.factoringBlocks.length > 0 &&
          Number(doc.factoringBlocks[0].availableSum) !== 0;
        const amount = isPartial
          ? Number(doc.factoringBlocks![0].availableSum)
          : (doc.total_delivery_sum_with_vat ?? 0);
        return sum + amount;
      }, 0);
      form.setValue('contractAmount', total.toFixed(2));
      form.setValue('buyerDebtAmount', total.toFixed(2));
    } else {
      form.setValue('contractAmount', '');
      form.setValue('buyerDebtAmount', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDocIds]);

  useEffect(() => {
    if (invoiceDocs.length) {
      const total = invoiceDocs.reduce((sum, doc) => {
        const isPartial =
          Array.isArray(doc.factoringBlocks) &&
          doc.factoringBlocks.length > 0 &&
          Number(doc.factoringBlocks[0].availableSum) !== 0;
        const amount = isPartial
          ? Number(doc.factoringBlocks![0].availableSum)
          : (doc.total_delivery_sum_with_vat ?? 0);
        return sum + amount;
      }, 0);
      form.setValue('contractAmount', total.toFixed(2));
      form.setValue('buyerDebtAmount', total.toFixed(2));
    } else {
      form.setValue('contractAmount', '');
      form.setValue('buyerDebtAmount', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDocIds]);

  const didoxModalRef = useRef<DidoxDocumentsModalRef>(null);
  const additionalDocsInputRef = useRef<HTMLInputElement>(null);

  const { data: copiedDeal } = useGetSellerRequestByIdQuery(id, { skip: !id });
  const isInternationalFactoring =
    sp.get('isInternationalFactoring') === 'true' ||
    Boolean(copiedDeal?.data.isInternational);
  const { data: buyers, isFetching: isBuyersFetching } =
    useGetFactorBuyersQuery({}, { skip: type !== 'seller' });
  const [getBuyers] = useLazyGetFactorBuyersQuery();
  const [getFactors] = useLazyGetFactorsQuery();

  const [getDocs] = useLazyGetDocsQuery();

  const [isCopying, setIsCopying] = useState(false);
  const [buyersOptions, setBuyersOptions] = useState<Buyer[]>([]);

  const fullscreenLoaderRef = useRef<BaseModalRef>(null);

  useCreateSellerRequestFormSideEffects({
    openLoader: () => fullscreenLoaderRef.current?.open(),
    closeLoader: () => fullscreenLoaderRef.current?.close(),
    form,
    setBuyersOptions,
    append,
  });

  useEffect(() => {
    if (buyers) {
      setBuyersOptions(buyers.data);
    }
  }, [buyers]);

  useEffect(() => {
    if (id && copiedDeal) {
      handleCopyDeal();
    }
  }, [id, copiedDeal]);

  const handleCopyDeal = async () => {
    if (!copiedDeal) return;
    try {
      setIsCopying(true);
      const docsToGet = copiedDeal.data.documents
        .filter((el) => el.didoxId)
        .map((el) => el.didoxId);

      const [brs, frs, docs] = await Promise.all([
        getBuyers(undefined, true).unwrap(),
        getFactors(
          copiedDeal.data.isInternational
            ? { international: true }
            : { companyTin: copiedDeal.data.buyer.tin },
          true
        ).unwrap(),
        ...(docsToGet.length
          ? [
              getDocs(
                {
                  factoringDocIds: docsToGet,
                  factoringPartyTin: profile?.tin || '',
                  owner: type === 'seller' ? 1 : 0,
                },
                true
              ).unwrap(),
            ]
          : [
              Promise.resolve({
                data: [],
                total: 0,
                next_page_url: 0,
                source: '',
              } as GetDidoxDocuments),
            ]),
      ]);

      const docsFromResponse = [...copiedDeal.data.documents];

      let sortedDocs = defaultDocuments.map(({ type }) => {
        const doc = docsFromResponse.find((d) => d.type === type) || null;
        if (doc) {
          docsFromResponse.splice(docsFromResponse.indexOf(doc), 1);
          return doc;
        }

        return { type };
      });

      sortedDocs = sortedDocs.concat(docsFromResponse);

      const didoxDocs: CreateSellerRequestValues['documents'] = sortedDocs.map(
        (doc) => {
          const defaultDoc = defaultDocuments.find((d) => d.type === doc.type);
          const docType = doc.type;
          if ('unique' in doc) {
            const didoxDoc = docs.data.find((d) => d.doc_id === doc.didoxId);
            if (didoxDoc && doc?.didoxId) {
              return {
                type: docType,
                required: !!defaultDoc?.required,
                value: {
                  ...didoxDoc,
                  unique: doc.unique,
                } as RequestDocumentDidoxType,
              };
            } else if (doc?.url) {
              return {
                value: {
                  value: doc.url,
                  unique: doc.unique,
                  name: doc.fileName,
                } as RequestDocumentFileType,
                required: !!defaultDoc?.required,
                type: docType,
              };
            }
          }

          return {
            value: null,
            type: docType,
            required: !!defaultDoc?.required,
          };
        }
      );

      const additionalDocsBase64 = copiedDeal.data.documents
        .filter((d) => d.url && d.type?.length > 3)
        .map((el) => ({
          value: el.url,
          name: el.fileName,
          unique: el.unique,
        }));

      setAdditionalDocs(additionalDocsBase64);
      setBuyersOptions(brs.data);

      const debtorValue = copiedDeal.data.debtorTin === profile?.tin ? 1 : 0;

      const isInternationalDeal = copiedDeal.data.isInternational;

      reset({
        buyer: isInternationalDeal
          ? copiedDeal.data.foreignContractorName
            ? { name: copiedDeal.data.foreignContractorName, tin: '' }
            : null
          : brs.data.find((b) => b.tin === copiedDeal.data.buyer.tin),
        factor: frs.data.find((f) => f.tin === copiedDeal.data.factor.tin),
        contractAmount: String(copiedDeal.data.contractAmount),
        buyerDebtAmount: String(copiedDeal.data.buyerDebtAmount),
        contractPaymentDueDate: dayjs(
          copiedDeal.data.contractPaymentDueDate,
          'DD.MM.YYYY'
        ),
        isDebtor: debtorOptions.find((el) => el.value === debtorValue),
        documents: didoxDocs,
        sellerTin: copiedDeal?.data?.seller?.tin,
        sellerName: isInternationalDeal
          ? (copiedDeal.data.foreignContractorName ?? '')
          : undefined,
        currency: copiedDeal.data.currency,
        foreignContractorId: copiedDeal.data.foreignContractorId ?? undefined,
      });
    } catch (err) {
      showNotification(getFetchEror(err));
    } finally {
      setIsCopying(false);
    }
  };

  const handleAdditionalDocsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;

    if (files) {
      if (files[0].type !== 'application/pdf') {
        return showNotification(t('errors:only_pdf_allowed'), 'error');
      }
      setAdditionalDocs((prev) => [
        ...prev,
        ...Array.from(files).map((file) => ({ value: file })),
      ]);
    }
  };

  const onAddAdditionalDocs = () => {
    additionalDocsInputRef.current?.click();
  };

  const handleRemoveAdditionalDocs = () => {
    setAdditionalDocs([]);
  };

  const handleMultipleDocsChange = (docs: DidoxDocument[]) => {
    const addedDocuments = getValues('documents');
    const existingDocuments = addedDocuments.filter((el) =>
      docs?.find((doc) => _.isEqual(doc, el.value))
    );

    const uniqueDocuments = docs.filter(
      (doc) => !existingDocuments.some((el) => _.isEqual(doc, el.value))
    );

    uniqueDocuments.forEach((doc) => {
      append({
        value: doc,
        type: '002',
        required: true,
        additionTypes: ['008'],
      });
    });

    if (existingDocuments.length) {
      const existingDocNames = existingDocuments
        .map((el) => el.value?.name)
        .join(', ');

      showNotification(
        t('errors:documets_already_added', { names: existingDocNames }),
        'warning'
      );
    }
  };

  const handleAddMoreDocuments = async () => {
    if (isInternationalFactoring) {
      append({
        value: null,
        type: '002',
        required: true,
        additionTypes: ['008'],
      });
      return;
    }

    const docs = await didoxModalRef.current?.open(['002', '008'], true);
    if (docs?.length) {
      handleMultipleDocsChange(docs);
    }
  };

  if (isCopying) {
    return <UILoader />;
  }

  return (
    <Box>
      <CreateSellerRequestTitle number={copiedDeal?.data.number} type={type} />
      <CreateSellerRequestStatus
        sx={{ mt: 2.5 }}
        status={copiedDeal?.data.status ?? 'draft'}
      />
      <Divider sx={{ my: 2.5 }} />

      {type === 'seller' ? (
        <RenderDealRows
          form={form}
          isInternationalFactoring={isInternationalFactoring}
          type='seller'
          buyersOptions={buyersOptions}
          setBuyersOptions={setBuyersOptions}
          isBuyersFetching={isBuyersFetching}
        />
      ) : (
        <RenderDealRows
          form={form}
          isInternationalFactoring={isInternationalFactoring}
          type='buyer'
        />
      )}

      <Stack spacing={2} mt={2.5}>
        {fields.map((f, index) => (
          <Stack spacing={1} key={f.id}>
            <Controller
              control={control}
              name={`documents.${index}.value`}
              rules={{
                required: f.required,
              }}
              render={({ field, fieldState }) => (
                <CreateDealChoseDocument
                  type={f.type}
                  required={f.required}
                  didoxModalRef={didoxModalRef}
                  value={field.value as DidoxDocument}
                  invalid={Boolean(fieldState.error)}
                  onChange={field.onChange}
                  onMultipleChange={handleMultipleDocsChange}
                  additionTypes={f.additionTypes}
                  disablePcButton={f.type === '002'}
                  disabled={(() => {
                    switch (f.type) {
                      case '002':
                      case '008':
                        return !isContractAdded;
                      case '052':
                      case '075':
                        return !isInvoiceAdded;
                      default:
                        return false;
                    }
                  })()}
                  onDelete={() => {
                    const docs = form.getValues('documents');
                    const doc = docs[index];
                    if (docs.filter((d) => d.type === doc.type).length > 1) {
                      remove(index);
                    } else {
                      field.onChange(null);
                    }
                  }}
                  partner={buyer || sellerTin}
                  getValues={getValues}
                  isInternationalFactoring={isInternationalFactoring}
                />
              )}
            />
          </Stack>
        ))}

        <Button
          color='inherit'
          sx={{ color: 'primary.main' }}
          onClick={handleAddMoreDocuments}
          disabled={type === 'seller' ? !buyer : !sellerTin}
        >
          {t('add_invoice')}
        </Button>
      </Stack>

      <Stack direction='row' mt={2} alignItems='center' spacing={1} height={40}>
        <Box
          sx={{
            border: '1px solid #E6F1FF',
            borderRadius: '6px',
            px: 2,
            flex: 1,
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {additionalDocs.length > 0 ? (
            additionalDocs.map((doc) => (
              <Box
                key={doc?.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#E6F1FF',
                  borderRadius: '6px',
                  p: 0.5,
                  mr: 1,
                }}
              >
                {typeof doc.value === 'string' ? doc?.name : doc?.value?.name}
              </Box>
            ))
          ) : (
            <UIFormLabel sx={{ m: 0 }}>{t('additional_documents')}</UIFormLabel>
          )}
          {additionalDocs.length > 0 && (
            <IconButton
              onClick={handleRemoveAdditionalDocs}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 4,
                transform: 'translateY(-50%)',
              }}
            >
              <Box component={XmarkIcon} />
            </IconButton>
          )}
        </Box>

        <Button color='inherit' onClick={onAddAdditionalDocs} size='small'>
          <PaperClipIcon />
        </Button>

        <input
          ref={additionalDocsInputRef}
          type='file'
          accept='.pdf'
          style={{ display: 'none' }}
          onChange={handleAdditionalDocsChange}
        />
      </Stack>

      <CreateSellerRequestActions
        sx={{ mt: 6 }}
        form={form}
        additinonalDocs={additionalDocs}
        copiedDeal={copiedDeal?.data}
        type={type}
      />

      <DidoxDocumentsModal
        ref={didoxModalRef}
        partnerTin={type === 'seller' ? buyer?.tin || '' : sellerTin || ''}
        documents={documents}
        type={type}
      />
      <FullscreenLoader ref={fullscreenLoaderRef} />
    </Box>
  );
};
